const DB_NAME = "trekkin-protected-zones";
const STORE_NAME = "datasets";
const DB_VERSION = 1;
const FALLBACK_PREFIX = "trekkin.protectedZones.";

function openDatabase() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("indexeddb_unavailable"));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME, { keyPath: "areaId" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error("indexeddb_open_failed"));
  });
}

function fallbackKey(areaId) {
  return `${FALLBACK_PREFIX}${String(areaId || "").trim().toLowerCase()}`;
}

function readFallback(areaId) {
  try {
    const raw = globalThis.localStorage?.getItem(fallbackKey(areaId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeFallback(record) {
  try {
    globalThis.localStorage?.setItem(fallbackKey(record.areaId), JSON.stringify(record));
    return true;
  } catch {
    return false;
  }
}

export async function loadProtectedZonesCache(areaId) {
  const normalizedAreaId = String(areaId || "").trim().toLowerCase();
  if (!normalizedAreaId) return null;
  const database = await openDatabase().catch(() => null);
  if (!database) return readFallback(normalizedAreaId);
  return new Promise((resolve) => {
    const transaction = database.transaction(STORE_NAME, "readonly");
    const request = transaction.objectStore(STORE_NAME).get(normalizedAreaId);
    request.onsuccess = () => resolve(request.result || readFallback(normalizedAreaId));
    request.onerror = () => resolve(readFallback(normalizedAreaId));
    transaction.oncomplete = () => database.close();
    transaction.onerror = () => database.close();
  });
}

export async function saveProtectedZonesCache({ areaId, geoJson, rulesVersion, sourceUpdatedAt }) {
  const normalizedAreaId = String(areaId || "").trim().toLowerCase();
  if (!normalizedAreaId || !geoJson) throw new Error("protected_zones_cache_invalid");
  const record = {
    areaId: normalizedAreaId,
    geoJson,
    rulesVersion: String(rulesVersion || ""),
    sourceUpdatedAt: sourceUpdatedAt || null,
    cachedAt: new Date().toISOString(),
  };
  const database = await openDatabase().catch(() => null);
  if (!database) {
    if (!writeFallback(record)) throw new Error("protected_zones_cache_write_failed");
    return record;
  }
  const stored = await new Promise((resolve) => {
    const transaction = database.transaction(STORE_NAME, "readwrite");
    transaction.objectStore(STORE_NAME).put(record);
    transaction.oncomplete = () => {
      database.close();
      resolve(true);
    };
    transaction.onerror = () => {
      database.close();
      resolve(false);
    };
  });
  if (!stored && !writeFallback(record)) throw new Error("protected_zones_cache_write_failed");
  return record;
}

